import { motion } from 'framer-motion';
import { useI18n } from '../i18n/I18nContext';
import { PublicLayout } from './BookPage';
import { Newspaper, Download, Phone, Mail, FileText } from 'lucide-react';

const spring = { type: 'spring', stiffness: 300, damping: 20 };

const DOCS = [
  { key: 2, src: '/docs/dossier-de-presse.pdf', file: 'on-croit-qu-on-joue_dossier-de-presse.pdf' },
];

const VISUALS = [
  { key: 1, src: '/images/flyer_masterclass.png', file: 'flyer-masterclass-on-croit-qu-on-joue.png' },
  { key: 2, src: '/images/header_roman_projet.webp', file: 'banniere-on-croit-qu-on-joue.webp' },
  { key: 3, src: '/images/couverture.jpg', file: 'jaquette-complete-on-croit-qu-on-joue.png', downloadSrc: '/images/jaquette-complete.png' },
  { key: 4, src: '/images/masterclass.png', file: 'fiche-auteur-dimitri-mfoumou-titi.png', downloadSrc: '/images/fiche-auteur.png' },
];

export default function PressPage() {
  const { t } = useI18n();
  return (
    <PublicLayout testid="press-page" pageKey="page_presse">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
        <p className="text-or-400 font-bold tracking-widest text-sm uppercase flex items-center gap-2">
          <Newspaper className="w-4 h-4" strokeWidth={2.5} /> {t('press.eyebrow')}
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-creme mt-3" data-testid="press-title">
          {t('press.title')}
        </h1>
        <p className="text-base sm:text-lg text-brume leading-relaxed mt-6 max-w-2xl" data-testid="press-intro">
          {t('press.intro')}
        </p>
      </motion.div>

      <h2 className="text-2xl font-bold text-creme font-fredoka mt-12 mb-6" data-testid="press-docs-title">
        {t('press.docsTitle')}
      </h2>
      <div className="grid gap-6 max-w-2xl">
        {DOCS.map((d, i) => (
          <motion.div key={d.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.08 + i * 0.06 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 hover:border-or-400/40 transition-colors"
            data-testid={`press-doc-${d.key}`}>
            <span className="w-14 h-14 shrink-0 rounded-2xl bg-or-400/15 flex items-center justify-center">
              <FileText className="w-7 h-7 text-or-400" strokeWidth={2.5} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-or-400 uppercase tracking-widest">{t(`press.aud${d.key}`)}</p>
              <p className="font-bold text-creme text-base">{t(`press.doc${d.key}`)}</p>
            </div>
            <a href={d.src} download={d.file} data-testid={`press-doc-download-${d.key}`}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-sm px-5 py-2.5 sm:shrink-0 transition-colors">
              <Download className="w-4 h-4" strokeWidth={2.5} /> {t('press.download')}
            </a>
          </motion.div>
        ))}
      </div>

      <h2 className="text-2xl font-bold text-creme font-fredoka mt-12 mb-6" data-testid="press-visuals-title">
        {t('press.visualsTitle')}
      </h2>
      <div className="grid sm:grid-cols-2 gap-6">
        {VISUALS.map((v, i) => (
          <motion.div key={v.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ ...spring, delay: 0.1 + i * 0.08 }}
            className="bg-nuit-800 border border-nuit-700 rounded-3xl overflow-hidden hover:border-or-400/40 transition-colors"
            data-testid={`press-visual-${v.key}`}>
            <img src={v.src} alt={t(`press.visual${v.key}`)} loading="lazy"
              className="w-full h-56 object-cover object-top" />
            <div className="p-5 flex items-center justify-between gap-3">
              <p className="font-bold text-creme text-sm">{t(`press.visual${v.key}`)}</p>
              <a href={v.downloadSrc || v.src} download={v.file} data-testid={`press-download-${v.key}`}
                className="inline-flex items-center gap-2 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-sm px-5 py-2.5 shrink-0 transition-colors">
                <Download className="w-4 h-4" strokeWidth={2.5} /> {t('press.download')}
              </a>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ ...spring, delay: 0.3 }}
        className="mt-12 bg-nuit-800 border border-or-400/30 rounded-3xl p-8 text-center gold-glow"
        data-testid="press-contact">
        <h2 className="text-2xl font-bold text-creme font-fredoka mb-2">{t('press.contactTitle')}</h2>
        <p className="text-or-400 font-bold mb-6">Dimitri M'Foumou-Titi</p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href="tel:+33698697558" data-testid="press-contact-tel"
            className="inline-flex items-center gap-2 rounded-full border border-nuit-600 bg-nuit-900 px-5 py-2.5 text-sm font-bold text-creme hover:border-or-400/50 hover:text-or-300 transition-colors">
            <Phone className="w-4 h-4 text-or-400" strokeWidth={2.5} /> +33 6 98 69 75 58
          </a>
          <a href="mailto:dimitri.mfoumou@gmail.com" data-testid="press-contact-email"
            className="inline-flex items-center gap-2 rounded-full border border-nuit-600 bg-nuit-900 px-5 py-2.5 text-sm font-bold text-creme hover:border-or-400/50 hover:text-or-300 transition-colors">
            <Mail className="w-4 h-4 text-or-400" strokeWidth={2.5} /> dimitri.mfoumou@gmail.com
          </a>
        </div>
      </motion.div>
    </PublicLayout>
  );
}
